import Cookies from "js-cookie"
import { SupportRole } from "../models/common/supportrole"
import { User } from "../models/user"
import { Terminal } from "../models/terminal"
import { Franchise } from "../models/common/franchise"
import { FranchiseBriefResponse } from "../models/transport/response/franchiseBrief"
import { SupportUsers } from "../../infrastructure/models/common/supportusers"
import { SupportUserBriefResponse } from "../../infrastructure/models/transport/response/supportUserBrief"
import { GetOrderResponse } from "../../infrastructure/models/transport/response/GetOrdersResponse"
import { FranchiseUsers } from "../models/common/FranchiseUsers"
import { FranchiseUserBriefResponse } from "../../infrastructure/models/transport/response/FranchiseUserBriefResponse"
/**
 * Manages user session data within the application.
 */
export interface SessionService {
  /**
   * Indicates whether a user is logged in.
   */
  isLoggedin: boolean

  /**
   * Retrieves or updates the current logged-in user.
   */
  user: User | null

  /**
   * Retrieves the list of terminals stored in the session.
   */
  terminals: Terminal[]

  /**
   * Retrieves the list of franchises stored in the session.
   */
  franchises: Franchise[]

  /**
   * Stores brief response for franchises
   */
  franchiseBrief: FranchiseBriefResponse[]

  /**
   * Current franchise to show details and update
   */
  currentFranchise: Franchise | null

  /**
  * To show Order details
  */
  orders: GetOrderResponse[] | null

  /** 
    * Updates the currently active user in session storage.
    *
    * This method persists the given user object in `SessionStorage` under a
    * predefined key (typically `"user"`). If a valid user object is provided,
    * it is serialized to JSON and saved. If `null` or `undefined` is provided,
    * the existing user entry is removed from storage.
  */
  setUser(user: any)

  /** 
   * Persists orders to SessionStorage. Stores as JSON string if non-null, 
   * removes SessionStorage entry if null is provided.
   */
  setOrders(orders: GetOrderResponse[] | null): void

  /**
   * Checks if the user has the specified role.
   * @param requiredRole - The required role to check.
   * @returns True if the user has the required role, false otherwise.
   */
  hasRole(requiredRole: SupportRole): boolean

  /**
   * Clears all session data, logging out the user.
   */
  resetStorage(): void

  /**
   * Stores multiple terminals in the session.
   * @param terminals - An array of terminals to store.
   */
  setTerminals(terminals: Terminal[]): void

  /**
   * Stores multiple franchises in the session.
   * @param franchises - An array of franchises to store.
   */
  setFranchises(franchises: Franchise[]): void

  /**
   * Stores multiple franchises in the session to show on dashboard.
   * @param franchises - An array of franchises brief to store.
   */
  setFranchisesBrief(franchises: FranchiseBriefResponse[]): void

  /**
   * Stores current franchises in the session to update on details screen.
   * @param franchises - A franchises to store.
   */
  setCurrentFranchise(franchise: Franchise | null)

  /**
   * Adds a single terminal to the session without removing existing ones.
   * @param terminal - The terminal to add.
   */
  addTerminal(terminal: Terminal): void

  /**
   * Adds a single franchise to the session without removing existing ones.
   * @param franchise - The franchise to add.
   */
  addFranchise(franchise: Franchise): void

  /** …other props… */
  supportUserBrief: SupportUserBriefResponse[]
  setSupportUserBrief(list: SupportUserBriefResponse[]): void
  addSupportUsers(user: SupportUsers): void
  getLoggedInUser(): Promise<User | null>

  franchiseUserBrief: FranchiseUserBriefResponse[] | null
  setFranchiseUserBrief(users: FranchiseUserBriefResponse[]): void
  addFranchiseUsers(user: FranchiseUsers): void
}

/**
 * Implementation of `SessionService` that manages user session data using `sessionStorage`.
 */
export class AppSessionService implements SessionService {
  private userKey = "appUser"
  private terminalKey = "appTerminals"
  private franchiseKey = "appFranchises"
  private franchiseBriefKey = "appFranchiseBrief"
  private currentFranchiseKey = "appCurrentFranchise"
  private supportUserKey = "appSupportUsers"
  private supportUserBriefKey = "appSupportUserBrief"
  private franchiseUserBriefKey = "franchiseUserBrief"
  private ordersKey = "orders"

  constructor() {
    const storedUser = localStorage.getItem("user")
    const storedToken = localStorage.getItem("token")
    this.user = storedUser ? JSON.parse(storedUser) : null
  }

  saveSession(user: any, token: string) {
    this.user = user
    localStorage.setItem("user", JSON.stringify(user))
    localStorage.setItem("token", token)
  }

  /**
   * Checks if a user is logged in by verifying the presence of a token.
   */
  get isLoggedin(): boolean {
    return Cookies.get("token") != null
  }

  /**
   * Retrieves the currently stored user session.
   */
  get user(): User | null {
    const data = sessionStorage.getItem(this.userKey)
    return data ? JSON.parse(data) : null
  }

  /**
 * Stores the current user information in both memory and `localStorage`.
 *
 * This method updates the in-memory `user` reference and also persists
 * the same data to `localStorage` (as a JSON string).  
 *
 */
  setUser(user: any) {
    this.user = user
    localStorage.setItem("user", JSON.stringify(user))
  }

  /**
     * Retrieves the currently stored user object from memory.
     *
     * This method returns the `user` value that was last set using {@link setUser}.
     * It does **not** re-read from `localStorage`.  
     * If the application has been reloaded or the user has not been set,
     * this will return `undefined` or `null` depending on initialization.
     */
  getUser() {
    return this.user
  }

  /**
   * Updates the stored user session.
   */
  set user(user: User | null) {
    if (user) {
      sessionStorage.setItem(this.userKey, JSON.stringify(user))
    } else {
      sessionStorage.removeItem(this.userKey)
    }
  }

  async getLoggedInUser(): Promise<User | null> {
    return Promise.resolve(this.user)
  }

  /**
   * Retrieves the list of terminals from session storage.
   */
  get terminals(): Terminal[] {
    const data = sessionStorage.getItem(this.terminalKey)
    return data ? JSON.parse(data) : []
  }

  get franchiseUsers(): FranchiseUsers[] {
    const data = sessionStorage.getItem(this.franchiseUserBriefKey)
    return data ? JSON.parse(data) : []
  }

  get franchiseUserBrief(): FranchiseUserBriefResponse[] {
    const data = sessionStorage.getItem("franchiseUserBrief")
    return data ? JSON.parse(data) : []
  }
  /**
   * Stores an array of terminals in session storage.
   * @param terminals - The list of terminals to store.
   */
  setTerminals(terminals: Terminal[]): void {
    sessionStorage.setItem(this.terminalKey, JSON.stringify(terminals))
  }

  /**
   * Adds a single terminal to the stored list without overwriting existing ones.
   * @param terminal - The terminal to add.
   */
  addTerminal(terminal: Terminal): void {
    const terminals = this.terminals
    terminals.push(terminal)
    this.setTerminals(terminals)
  }

  /**
   * Retrieves the list of franchises from session storage.
   */
  get franchises(): Franchise[] {
    const data = sessionStorage.getItem(this.franchiseKey)
    return data ? JSON.parse(data) : []
  }

  /**
   * Stores an array of franchises in session storage.
   * @param franchises - The list of franchises to store.
   */
  setFranchises(franchises: Franchise[]): void {
    sessionStorage.setItem(this.franchiseKey, JSON.stringify(franchises))
  }

  /**
   * Retrieves the list of franchises from session storage for brief display.
   */
  get franchiseBrief(): FranchiseBriefResponse[] {
    const data = sessionStorage.getItem(this.franchiseBriefKey)
    if (!data || data === "undefined") {
      return []
    }

    try {
      return JSON.parse(data)
    } catch (e) {
      console.error("Failed to parse franchiseBrief from sessionStorage:", e)
      return []
    }
  }

  /**
   * Stores an array of franchises brief in session storage.
   * @param franchises - The list of franchises brief to store.
   */
  setFranchisesBrief(franchises: FranchiseBriefResponse[]): void {
    if (!franchises || !Array.isArray(franchises)) {
      console.warn("Invalid franchises data, skipping storage")
      return
    }
    sessionStorage.setItem(this.franchiseBriefKey, JSON.stringify(franchises))
  }

  get currentFranchise(): Franchise | null {
    const data = sessionStorage.getItem(this.currentFranchiseKey)
    return data ? JSON.parse(data) : null
  }

  setCurrentFranchise(franchise: Franchise | null) {
    if (franchise == null) {
      sessionStorage.removeItem(this.currentFranchiseKey)
      return
    }
    sessionStorage.setItem(this.currentFranchiseKey, JSON.stringify(franchise))
  }

  /**
   * Adds a single franchise to the stored list without overwriting existing ones.
   * @param franchise - The franchise to add.
   */
  addFranchise(franchise: Franchise): void {
    const franchises = this.franchises
    franchises.push(franchise)
    this.setFranchises(franchises)
  }

  get supportusers(): SupportUsers[] {
    const data = sessionStorage.getItem(this.supportUserKey)
    return data ? JSON.parse(data) : []
  }
  setSupportUsers(list: SupportUsers[]): void {
    sessionStorage.setItem(this.supportUserKey, JSON.stringify(list))
  }
  addSupportUsers(user: SupportUsers): void {
    const list = this.supportusers
    list.push(user)
    this.setSupportUsers(list)
  }

  get supportUserBrief(): SupportUserBriefResponse[] {
    const data = sessionStorage.getItem(this.supportUserBriefKey)
    return data ? JSON.parse(data) : []
  }
  setSupportUserBrief(list: SupportUserBriefResponse[]): void {
    sessionStorage.setItem(this.supportUserBriefKey, JSON.stringify(list))
  }

  setFranchiseUsers(list: FranchiseUsers[]): void {
    sessionStorage.setItem(this.franchiseUserBriefKey, JSON.stringify(list))
  }

  addFranchiseUsers(user: FranchiseUsers): void {
    const list = this.franchiseUsers || []
    list.push(user)
    this.setFranchiseUsers(list)
  }

  setFranchiseUserBrief(users: FranchiseUserBriefResponse[]): void {
    if (users) {
      sessionStorage.setItem(this.franchiseUserBriefKey, JSON.stringify(users))
    }
  }

  get orders(): GetOrderResponse[] | null {
    const data = sessionStorage.getItem(this.ordersKey)
    return data ? JSON.parse(data) : null
  }

  setOrders(orders: GetOrderResponse[] | null): void {
    if (orders) {
      sessionStorage.setItem(this.ordersKey, JSON.stringify(orders))
    } else {
      sessionStorage.removeItem(this.ordersKey)
    }
  }

  /**
   * Checks if the logged-in user has the specified role.
   * @param requiredRole - The role to check.
   * @returns True if the user has the role, otherwise false.
   */
  hasRole(requiredRole: SupportRole): boolean {
    return this.user?.role === requiredRole
  }

  /**
   * Clears all session data, removing the user, terminals, franchises, and authentication token.
   */
  resetStorage(): void {
    this.user = null
    sessionStorage.removeItem(this.terminalKey)
    sessionStorage.removeItem(this.franchiseKey)
    sessionStorage.removeItem(this.franchiseBriefKey)
    sessionStorage.removeItem(this.currentFranchiseKey)
    sessionStorage.removeItem(this.supportUserKey)
    sessionStorage.removeItem(this.supportUserBriefKey)
    sessionStorage.removeItem(this.ordersKey)
    localStorage.removeItem("user")
    Cookies.remove("token")
  }
}